/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { TypescriptComponent } from 'components/typescriptExample'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { FeatureStory } from '../featurestory'
import { StoryTeaser } from '../storyteaser'
import { EmojiReactions } from '../emojireactions'
import { Spring, easings } from 'react-spring/renderprops.cjs'
import renderHTML from 'react-render-html'
import { Link } from '@ta-interaktiv/contentbricks'

const ARTICLE_ID = '282399875307'
const teaserRefs = []
const params = new URL(window.location).searchParams

export class AppComponent extends React.Component<Props, State> {
  constructor() {
    super()
    this.state = {
      storyData: [],
      publicationDate: '5. Mai 2022',
      nextPublish: 'not set',
      mainStory: [],
      archivedStories: [],
      activeStory: 0,
      activeTeaserRef: 0,
      storyOpen: false,
      storyDisplayed: false,
      isTablet: window.innerWidth < 1000 && window.innerWidth > 599,
      isMobile: window.innerWidth < 600,
      preselectedStory: params.get('story') ? params.get('story') : null
    }
    this.updateDimensions = this.updateDimensions.bind(this)
    this.openStory = this.openStory.bind(this)
    this.closeStory = this.closeStory.bind(this)
    this.hideStoryWrapper = this.hideStoryWrapper.bind(this)
  }

  openStory(storyID, teaserID, storyTitle) {
    // console.log('openStory')
    this.setState({
      storyOpen: true,
      activeStory: storyID,
      activeTeaserRef: teaserID
    })
    document.body.classList.add('freeze')

    // track in google analytics
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'Interactions',
      event_action: 'click',
      event_label: `open-story: ` + storyTitle
    })
  }

  closeStory() {
    this.setState({ storyOpen: false })
    // update reactions in story teaser
    /*
    console.log('activeStory:', this.state.activeStory)
    console.log('activeTeaserRef:', this.state.activeTeaserRef)
    console.log('archivedStories:', this.state.archivedStories)
    */
    const teaserIndex = this.state.activeTeaserRef
    teaserRefs[teaserIndex].current.updateReactionCount()
  }

  hideStoryWrapper() {
    this.setState({ storyDisplayed: false })
    document.body.classList.remove('freeze')
  }

  updateDimensions() {
    // console.log('updateDimensions')
    this.setState({ isMobile: window.innerWidth < 600 })
    this.setState({
      isTablet: window.innerWidth < 1000 && window.innerWidth > 599
    })
  }

  extractPublicationDate(rawData) {
    // extract publication date
    const months = [
      'Januar',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember'
    ]
    const dateObject = new Date(rawData.updated)
    const d = dateObject.getDate()
    const m = dateObject.getMonth()
    const y = dateObject.getFullYear()
    return d + '. ' + months[m] + ' ' + y
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions)

    // console.log('componentDidMount')
    /* GET SHEET DATA VIA CLOUD STORAGE */
    fetch(
      'https://storage.googleapis.com/ta-interaktiv/kennenlerngeschichten/geschichten.json'
    )
      .then(response => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error('Cloud Function Data was not OK')
        }
      })
      .then(responseData => {
        // console.log('responseData:', responseData)
        this.setState({ storyData: responseData.data }, function() {
          // console.log('storyData', this.state.storyData)

          const indexedStories = this.state.storyData.map(function(
            story,
            index
          ) {
            story.index = index
            const id = story.Titel.replace(/\s/g, '').toLowerCase()
            story.id = id
            return story
          })
          const featuredStories = indexedStories.filter(
            stories => stories.featured === 'TRUE'
          )
          const mainStory = featuredStories.map(
            function(story, index) {
              return (
                <FeatureStory
                  person={story.Person}
                  isRealName={story.Person.includes('*')}
                  title={story.Titel}
                  image={
                    'https://storage.googleapis.com/ta-interaktiv/kennenlerngeschichten/images/' +
                    story.Bild
                  }
                  storyID={story.id}
                  articleID={ARTICLE_ID}
                  reactToStory={this.reactToStory}
                >
                  {story.Text}
                </FeatureStory>
              )
            }.bind(this)
          )
          this.setState({ mainStory: mainStory })
          const archivedStories = indexedStories
            .filter(
              stories => stories.publiziert === 'TRUE' // && stories.featured !== 'TRUE'
            )
            .map(
              function(story, index) {
                const newRef = React.createRef()
                teaserRefs.push(newRef)
                return (
                  <StoryTeaser
                    person={story.Person}
                    isRealName={story.Person.includes('*')}
                    title={story.Titel}
                    image={
                      'https://storage.googleapis.com/ta-interaktiv/kennenlerngeschichten/images/' +
                      story.Bild
                    }
                    openStory={this.openStory}
                    index={story.index}
                    teaserIndex={index}
                    id={story.id}
                    articleID={ARTICLE_ID}
                    ref={newRef}
                    key={'teaser-' + index}
                  >
                    {story.Lead}
                  </StoryTeaser>
                )
              }.bind(this)
            )
          this.setState(
            { archivedStories: archivedStories.reverse() },
            function() {
              // open a story preselected via URL?
              if (this.state.preselectedStory !== null) {
                console.log('preselected Story:', this.state.preselectedStory)
                console.log('teaserRefs:', teaserRefs)
                console.log('teaserRefs.length:', teaserRefs.length)
                if (
                  this.state.preselectedStory < teaserRefs.length &&
                  this.state.preselectedStory > 0
                ) {
                  this.openStory(
                    teaserRefs[this.state.preselectedStory].current.props.index,
                    teaserRefs[this.state.preselectedStory].current.props
                      .teaserIndex,
                    teaserRefs[this.state.preselectedStory].current.props.title
                  )
                }
              }
            }.bind(this)
          )
        })
        this.setState({
          publicationDate: this.extractPublicationDate(responseData),
          nextPublish: responseData.nextPublish
        })
      })
  }

  render() {
    return (
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} />
        <ThemeSwitch />

        <header className='ui vertical  very fitted  segment'>
          <div className='ui container'>
            <div className='ui header'>
              <h1>Ihre schönsten Kennenlerngeschichten</h1>
              <h2>
                Wir wollten wissen, wie Sie sich verliebt haben. Lesen Sie hier
                eine Auswahl der berührendsten Einsendungen.
              </h2>
            </div>
          </div>
        </header>

        <div className='ui vertical segment'>
          <div className='ui container'>
            <div className='ta metainfo'>
              <div className='ta author'>
                Katharina Graf, Mirja Gabathuler, Tina Huber
              </div>
              <div className='ta updated'>
                Aktualisiert am {this.state.publicationDate}
              </div>
            </div>
          </div>
        </div>

        {/* Featured Story */}
        {/* this.state.mainStory[0] */}

        <div
          className='ui vertical segment'
          style={
            {
              /*
            backgroundColor: '#F8F7F4',
            borderTop: '2px solid #9698a8'
            */
            }
          }
        >
          {/*
          <div className='ui text container'>
            <div className='ta text'>
              <div className='hint'>
                <div className='top'>
                  <div className='icon'>
                    <img
                      className='ui fluid image'
                      src={require('../../images/icon-hint.svg')}
                    />
                  </div>
                  <div className='right'>
                    Die nächste Kennenlerngeschichte lesen Sie am kommenden{' '}
                    {this.state.nextPublish}.
                  </div>
                </div>
              </div>
            </div>
          </div>
        */}

          {this.state.archivedStories.length > 0 && (
            <div className='ui container'>
              <div className='ta text'>
                {/* <h5>Weitere Leserinnen und Leser erzählen</h5> */}
                <div
                  className={
                    this.state.isTablet
                      ? 'ui stackable two column grid'
                      : 'ui stackable three column grid'
                  }
                >
                  {this.state.archivedStories}
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          className='ui vertical segment'
          style={
            {
              // backgroundColor: '#F8F7F4'
            }
          }
        >
          {/*
          <div className='ui text container'>
            <div className='ta text'>
              <div className='call'>
                <div className='top'>
                  <div className='icon'>
                    <img
                      className='ui fluid image'
                      src={require('../../images/icon-call.svg')}
                    />
                  </div>
                  <div className='right'>
                    <h6>Leseraufruf</h6>
                    <div className='title'>
                      Haben Sie einen Lieblingsmenschen?
                    </div>
                  </div>
                </div>
                <div className='txt'>
                  Ein Freund, eine Nachbarin, die Schwester: Wir möchten
                  Geschichten von nicht-romantischen Beziehungen hören, die Ihr
                  Leben prägen.
                  <br />
                  <Link href='315214768503'>
                    Erzählen Sie uns Ihre Geschichte
                  </Link>
                </div>
              </div>
            </div>
          </div>
          */}
        </div>

        <Footer
          hidePortfolioTeaser
          hideTopHalf
          articleId={ARTICLE_ID}
          questions={[
            'Wie unterhaltsam war der Beitrag?',
            'Wie gefällt Ihnen das Format der Lesergeschichten?',
            'Wie gefällt Ihnen die Gestaltung?'
          ]}
          credits={[
            [
              'Redaktion',
              '<nobr>Mirja Gabathuler,</nobr> <nobr>Katharina Graf</nobr>, <nobr>Tina Huber</nobr>'
            ],
            ['Illustration', 'Micha Treuthardt'],
            ['Gestaltung und Umsetzung', 'Mathias Lutz']
          ]}
        />

        <Spring
          from={{
            number: this.state.storyOpen
              ? 0
              : this.state.isTablet || this.state.isMobile
              ? 100
              : 90
          }}
          to={{
            number: this.state.storyOpen
              ? this.state.isTablet || this.state.isMobile
                ? 100
                : 90
              : 0
          }}
          onRest={() =>
            this.state.storyOpen
              ? this.setState({ storyDisplayed: true })
              : this.hideStoryWrapper()
          }
          config={{
            duration: this.state.storyOpen ? 250 : 200
          }}
        >
          {props => (
            <div
              className={
                this.state.storyOpen
                  ? 'story-wrapper visible'
                  : this.state.storyDisplayed
                  ? 'story-wrapper visible'
                  : 'story-wrapper'
              }
            >
              <div
                style={{
                  opacity: this.state.storyOpen
                    ? '1'
                    : this.state.storyDisplayed
                    ? '0'
                    : '1'
                }}
                className='dimmer'
                onClick={this.closeStory}
              />
              <div
                className='story-close'
                style={{
                  opacity: this.state.storyOpen
                    ? this.state.storyDisplayed
                      ? '1'
                      : '0'
                    : '0'
                }}
              >
                <div className='close' onClick={this.closeStory} />
              </div>
              <div
                className='ui vertical story segment'
                style={{ height: Math.round(props.number) + '%' }}
              >
                {this.state.storyOpen && (
                  <div className='ui text container'>
                    <div className='ta text'>
                      <h4>
                        {this.state.storyData[this.state.activeStory].Person}
                      </h4>
                      <h3>
                        {this.state.storyData[this.state.activeStory].Titel}
                      </h3>
                      <div
                        className='illustration'
                        style={{ marginBottom: '1em' }}
                      >
                        <img
                          className='ui fluid image'
                          src={
                            'https://storage.googleapis.com/ta-interaktiv/kennenlerngeschichten/images/' +
                            this.state.storyData[this.state.activeStory].Bild
                          }
                        />
                      </div>
                      <div>
                        {renderHTML(
                          this.state.storyData[this.state.activeStory].Text
                        )}
                        {this.state.storyData[
                          this.state.activeStory
                        ].Person.includes('*') && (
                          <div className='note'>*Name geändert</div>
                        )}
                      </div>
                    </div>
                    <EmojiReactions
                      storyID={this.state.storyData[
                        this.state.activeStory
                      ].Titel.replace(/\s/g, '').toLowerCase()}
                      articleID={ARTICLE_ID}
                      storyIndex={this.state.activeStory}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </Spring>
      </div>
    )
  }
}
